import { Breakpoints } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { bytesToSize, getBase64SrcFromBlob } from '@atmi/core/utils';

import { BehaviorSubject, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { TableField, TableSelection, UiTableComponent } from '@ui/ui.module';

import { MediaModuleConfiguration } from '@sim/media/models/configuration';
import { MediaConfigurationService } from '@sim/media/token';

interface State<T> {
  fields: Array<TableField<T>>;
  selectedItems: ExtendedFile[];
}

interface ExtendedFile {
  file: File;
  url?: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-media-to-upload',
  styleUrls: ['./media-to-upload.component.scss'],
  templateUrl: './media-to-upload.component.html',
})
export class MediaToUploadComponent implements UiTableComponent<ExtendedFile> {

  @Input() set files(v: File[] | undefined) {
    this.files$.next(v);

    this.setState({ selectedItems: [] });
  }
  get files() {
    return this.files$.value;
  }
  private readonly files$ = new BehaviorSubject<File[] | undefined>(undefined);

  @Output() readonly delete = new EventEmitter<File[]>();
  @Output() readonly upload = new EventEmitter<File[]>();

  readonly state$ = new BehaviorSubject<State<ExtendedFile>>({
    fields: [
      {
        header: '',
        hideBreakpoints: [Breakpoints.XSmall],
        key: 'chekbox',
        selectable: true,
      },
      {
        header: this.translate.instant('Nome file'),
        key: 'file.name',
      },
      {
        header: this.translate.instant('Tipo'),
        hideBreakpoints: [Breakpoints.XSmall],
        key: 'file.type',
      },
      {
        context: (x) => bytesToSize(x.file.size),
        header: this.translate.instant('Dimensione'),
        hideBreakpoints: [Breakpoints.XSmall, Breakpoints.Small],
        key: 'file.size',
      },
      {
        context: (x) => x.file.type === 'application/pdf'
          ? this.config.pdfPlaceholderPath
          : x.url,
        header: '',
        key: 'url',
        template: 'img',
      },
    ],
    selectedItems: [],
  });

  get state() {
    return this.state$.value;
  }

  get fields() {
    return this.state$.value.fields;
  }

  readonly pager$ = () =>
    this.files$.pipe(
      switchMap((files) => {
        if (!files) {
          return of([]);
        }

        const promise = files.map(async (f) => {
          const url = await getBase64SrcFromBlob(f);

          return {
            file: f,
            url,
          };
        });
        return Promise.all(promise);
      }),
      map((files) => ({ data: files, count: files.length }))
    )

  constructor(
    private readonly translate: TranslateService,
    @Inject(MediaConfigurationService) private readonly config: MediaModuleConfiguration,
  ) {
  }

  selectHandler(selectedNews: TableSelection<ExtendedFile>) {
    this.setState({ selectedItems: selectedNews.selected });
  }

  uploadHandler() {
    const filesToUpload = this.state.selectedItems.length > 0
      ? this.state.selectedItems.map((i => i.file))
      : this.files;

    if (filesToUpload) {
      this.upload.emit(filesToUpload);
    }
  }

  deleteSelectedHandler(items: ExtendedFile[]) {
    const files = items.map((i) => i.file);
    this.delete.emit(files);
  }

  private setState(state: Partial<State<ExtendedFile>>) {
    this.state$.next({ ...this.state, ...state });
  }
}
